"use client";

import { AlertTriangle, CheckCircle2, Coins, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@punchup/utils";
import dayjs from "@punchup/utils/dayjs";

import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { EmbeddedStripeCheckout } from "../../payments/stripe-checkout";
import { BuyCreditsTabProps, type CreditTier, PurchaseMode } from "./types";
import { CREDITS_PAYMENT_SUCCESS_COPY, getTierCards } from "./constants";
import {
  MobileTierChangeConfirmScreen,
  TierChangeConfirmDialog,
  type TierChangePreviewStatus,
} from "./TierChangeConfirmDialog";

/** Reveal anyway if Stripe never reports ready, so Cancel is never out of reach. */
export const STRIPE_READY_FALLBACK_MS = 4000;

const collapseClass = (isHidden: boolean) =>
  cn(
    "grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none",
    isHidden ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
  );

function SpinnerButton({
  label,
  ...props
}: { label: string } & React.ComponentProps<typeof Button>) {
  return (
    <Button variant="punchupInverted" className="w-full" disabled {...props}>
      <span className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        {label}
      </span>
    </Button>
  );
}

export function BuyCreditsTab({
  onFetchCheckoutSession,
  onPaymentComplete,
  userEmail,
  activeSubscription,
  onChangeTier,
  onPreviewTierChange,
  initialSelection,
  isDesktop,
  balance,
  footerPortalRef,
  updatedPricing = false,
}: BuyCreditsTabProps) {
  // Subscription tier changes are generation-sticky (D10): an active
  // subscriber sees their OWN pricing generation, never the gate. One-time
  // purchases always follow the gate. The two sets can legitimately differ
  // for a legacy subscriber viewing this modal while the gate is on.
  const subGenUpdated = activeSubscription
    ? (activeSubscription.updatedGeneration ?? false)
    : updatedPricing;
  const subscriptionCards = getTierCards(subGenUpdated);
  const oneTimeCards = getTierCards(updatedPricing);
  const [activePurchaseMode, setActivePurchaseMode] =
    useState<PurchaseMode | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isFetchingSecret, setIsFetchingSecret] = useState(false);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const revealCheckout = useCallback(() => setCheckoutReady(true), []);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedOption, setSelectedOption] = useState<{
    tier: CreditTier;
    mode: PurchaseMode;
  }>({
    tier: "starter",
    mode: activeSubscription ? "one_time" : "subscription",
  });

  useEffect(() => {
    if (initialSelection) setSelectedOption(initialSelection);
  }, [initialSelection]);

  const [tierChangeState, setTierChangeState] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [tierChangeError, setTierChangeError] = useState<string | null>(null);
  const [showTierChangeConfirm, setShowTierChangeConfirm] = useState(false);
  const [previewStatus, setPreviewStatus] =
    useState<TierChangePreviewStatus>("idle");
  const [previewedAmountCents, setPreviewedAmountCents] = useState<
    number | undefined
  >(undefined);

  const selectedCards =
    selectedOption.mode === "subscription" ? subscriptionCards : oneTimeCards;
  const selectedTier = selectedCards.find(
    (c) => c.tier === selectedOption.tier
  )!;
  // Stripe mounts collapsed and the idle layout holds until it is ready, so the switch is one transition.
  const awaitingStripe = activePurchaseMode !== null && !checkoutReady;
  const checkoutActive = activePurchaseMode !== null && checkoutReady;
  const isPreparing = isFetchingSecret || awaitingStripe;

  useEffect(() => {
    if (!awaitingStripe) return;
    const timeout = setTimeout(revealCheckout, STRIPE_READY_FALLBACK_MS);
    return () => clearTimeout(timeout);
  }, [awaitingStripe, revealCheckout]);

  const isTierChange =
    !!activeSubscription &&
    selectedOption.mode === "subscription" &&
    selectedOption.tier !== activeSubscription.tier;
  const isUpgrade = isTierChange && activeSubscription.tier === "starter";
  const isDowngrade = isTierChange && activeSubscription.tier === "pro";
  const ctaButtonText = isUpgrade
    ? "Upgrade"
    : isDowngrade
      ? "Downgrade"
      : "Buy Now";

  const handleRadioChange = (tier: CreditTier, mode: PurchaseMode) => {
    setSelectedOption({ tier, mode });
    if (tierChangeState !== "idle") {
      setTierChangeState("idle");
      setTierChangeError(null);
    }
    if (showTierChangeConfirm) {
      setShowTierChangeConfirm(false);
      setPreviewStatus("idle");
      setPreviewedAmountCents(undefined);
    }
  };

  const handleBuyNow = async () => {
    setClientSecret(null);
    setFetchError(null);
    setCheckoutReady(false);
    setIsFetchingSecret(true);
    try {
      const { clientSecret: secret } = await onFetchCheckoutSession(
        selectedTier.tier,
        selectedOption.mode
      );
      setClientSecret(secret);
      setActivePurchaseMode(selectedOption.mode);
    } catch (err) {
      setFetchError(
        err instanceof Error
          ? err.message
          : "Could not load checkout. Check your connection and try again."
      );
    } finally {
      setIsFetchingSecret(false);
    }
  };

  const handleTierChange = async () => {
    if (!onChangeTier) return;
    setTierChangeState("loading");
    setTierChangeError(null);
    try {
      await onChangeTier(selectedOption.tier);
      setTierChangeState("success");
    } catch (err) {
      setTierChangeError(
        err instanceof Error
          ? err.message
          : "Could not update your plan. Please try again."
      );
      setTierChangeState("error");
    }
  };

  const handleTierChangeCtaClick = () => {
    if (!updatedPricing) {
      // Gate off: today's behavior, byte-for-byte — no confirmation dialog.
      void handleTierChange();
      return;
    }
    setShowTierChangeConfirm(true);
    if (isUpgrade && onPreviewTierChange) {
      setPreviewStatus("loading");
      setPreviewedAmountCents(undefined);
      onPreviewTierChange(selectedOption.tier)
        .then((amountDueCents) => {
          setPreviewedAmountCents(amountDueCents);
          setPreviewStatus("ready");
        })
        .catch(() => {
          // Fail-open: the dialog falls back to the "at least" range copy.
          setPreviewStatus("error");
        });
    }
  };

  const confirmTierChange = () => {
    setShowTierChangeConfirm(false);
    void handleTierChange();
  };

  const handleCancel = () => {
    setClientSecret(null);
    setFetchError(null);
    setActivePurchaseMode(null);
    setCheckoutReady(false);
    setIsSubmitting(false);
  };

  // Session-fetch spinner/error render via renderCTA: no purchase mode is set until the secret exists.
  const renderCheckoutContent = () => {
    if (!clientSecret) return null;
    return (
      <EmbeddedStripeCheckout
        clientSecret={clientSecret}
        formId={`checkout-form-${activePurchaseMode}`}
        onPaymentComplete={() => {
          setPaymentSuccess(true);
          onPaymentComplete();
        }}
        onSubmittingChange={setIsSubmitting}
        onReady={revealCheckout}
        onLoadError={revealCheckout}
        userEmail={userEmail}
        successMessage={CREDITS_PAYMENT_SUCCESS_COPY}
      />
    );
  };

  const renderSection = (
    sectionMode: PurchaseMode,
    title: string,
    options?: { trailingDivider?: boolean }
  ): React.ReactNode => {
    const isHidden = checkoutActive && activePurchaseMode !== sectionMode;
    const cards =
      sectionMode === "subscription" ? subscriptionCards : oneTimeCards;

    return (
      <div className={collapseClass(isHidden)} aria-hidden={isHidden}>
        <div className="overflow-hidden">
          <div data-testid={`section-${sectionMode}`}>
            <h3 className="text-sm font-normal tracking-wide text-primary-blue mb-1">
              {title}
            </h3>
            {cards.map((card) => {
              const isActiveSub =
                sectionMode === "subscription" &&
                activeSubscription?.tier === card.tier;
              const isSelected =
                selectedOption.tier === card.tier &&
                selectedOption.mode === sectionMode;
              const isCardHidden = checkoutActive && !isSelected;

              return (
                <div
                  key={card.tier}
                  className={collapseClass(isCardHidden)}
                  aria-hidden={isCardHidden}
                >
                  <div className="overflow-hidden">
                    <label
                      className={cn(
                        "mt-2 flex items-center gap-3 rounded-lg border p-3 relative transition-[border-color,background-color,opacity] duration-200 ease-out",
                        isActiveSub
                          ? "border-[#A4A6A8] bg-[#F9FAFB] opacity-75 cursor-default"
                          : isSelected
                            ? "bg-orange-50 border-orange-200 cursor-pointer"
                            : "bg-white border-gray-100 cursor-pointer"
                      )}
                    >
                      {isActiveSub && (
                        <Badge className="absolute top-2 right-2 bg-[#FE5E25]/10 text-[#FE5E25] border-0 text-xs">
                          Current Plan
                        </Badge>
                      )}
                      {!checkoutActive && !isActiveSub && (
                        <input
                          type="radio"
                          name="credit-tier"
                          value={`${card.tier}-${sectionMode}`}
                          checked={isSelected}
                          disabled={isPreparing}
                          onChange={() =>
                            handleRadioChange(card.tier, sectionMode)
                          }
                          className="accent-[#FE5E25]"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-normal text-primary-blue">
                          {sectionMode === "subscription"
                            ? `${card.credits}/mo`
                            : card.credits}
                        </p>
                        <p className="text-xs text-[#787B7E]">
                          {card.features.join(" or ")}
                        </p>
                        {isActiveSub && activeSubscription?.renewalDate && (
                          <span className="text-xs text-[#787B7E]">
                            Next Refill:{" "}
                            {dayjs(activeSubscription.renewalDate).format(
                              "MMM D"
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-0.5 shrink-0 ml-2">
                        {sectionMode === "subscription" &&
                        activeSubscription?.tier === "starter" &&
                        card.tier === "pro" ? (
                          <div className="flex flex-col items-end">
                            <span className="line-through text-xs text-[#787B7E] font-normal">
                              USD {card.amount}.00
                            </span>
                            {/* Upgrade delta derived from the rendered
                                generation's prices (pro − starter) so it can
                                never contradict the struck-through price
                                above it: $45 legacy, $180 updated. */}
                            <p className="text-sm font-semibold text-primary-blue whitespace-nowrap">
                              {`USD ${
                                card.amount -
                                cards.find((c) => c.tier === "starter")!.amount
                              }.00/mo`}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm font-semibold text-primary-blue whitespace-nowrap">
                            {sectionMode === "one_time"
                              ? `USD ${card.amount}.00`
                              : `USD ${card.amount}.00/mo`}
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
          {options?.trailingDivider && <hr className="border-gray-200 mt-4" />}
        </div>
      </div>
    );
  };

  const renderCTA = (): React.ReactNode => {
    if (isTierChange && !checkoutActive) {
      if (tierChangeState === "success") {
        return (
          <div className="flex flex-col items-center gap-3 py-4 text-center animate-fade-in motion-reduce:animate-none">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="font-semibold">Subscription updated!</p>
            <p className="text-sm text-muted-foreground">
              You&apos;re now on the {selectedOption.tier} plan.
            </p>
          </div>
        );
      }
      if (tierChangeState === "loading") {
        return <SpinnerButton label="Processing..." />;
      }
      if (tierChangeState === "error") {
        return (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-2 animate-fade-in motion-reduce:animate-none">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-red-600">
              <p>
                {tierChangeError ??
                  "Could not update your plan. Please try again."}
              </p>
              <button
                type="button"
                onClick={handleTierChange}
                className="underline text-sm text-red-600 mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        );
      }
      return (
        <Button
          variant="punchupInverted"
          className="w-full"
          onClick={handleTierChangeCtaClick}
        >
          {ctaButtonText}
        </Button>
      );
    }

    if (checkoutActive && !paymentSuccess) {
      return (
        <div className="space-y-2">
          <Button
            type="submit"
            form={`checkout-form-${activePurchaseMode}`}
            disabled={isSubmitting}
            variant="punchupInverted"
            className="w-full"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </span>
            ) : (
              `Pay $${selectedTier.amount}`
            )}
          </Button>
          <button
            type="button"
            onClick={handleCancel}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            Cancel
          </button>
        </div>
      );
    }

    if (checkoutActive) return null;

    return (
      <>
        {fetchError && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-2 animate-fade-in motion-reduce:animate-none">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <p className="flex-1 text-sm text-red-600">{fetchError}</p>
          </div>
        )}
        <Button
          variant="punchupInverted"
          className="w-full"
          onClick={handleBuyNow}
          disabled={isPreparing}
        >
          {isPreparing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Setting up checkout...
            </span>
          ) : (
            ctaButtonText
          )}
        </Button>
      </>
    );
  };

  if (showTierChangeConfirm && isDesktop === false && isTierChange) {
    return (
      <MobileTierChangeConfirmScreen
        direction={isUpgrade ? "upgrade" : "downgrade"}
        cards={subscriptionCards}
        renewalDate={activeSubscription?.renewalDate ?? null}
        onBack={() => setShowTierChangeConfirm(false)}
        onConfirm={confirmTierChange}
        previewedAmountCents={previewedAmountCents}
        previewStatus={previewStatus}
      />
    );
  }

  return (
    <div className="space-y-4">
      {isDesktop !== false && (
        <h2 className="text-xl font-semibold text-primary-blue">
          {isUpgrade
            ? "Upgrade Subscription"
            : isDowngrade
              ? "Downgrade Subscription"
              : "Buy Credits"}
        </h2>
      )}
      {isDesktop === false && balance && (
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#38A93C]/10 px-3 py-1 w-fit">
            <Coins className="h-3.5 w-3.5 text-[#38A93C]" />
            <span className="text-sm font-semibold text-[#38A93C]">
              {balance.totalBalance.toLocaleString()} credits remaining
            </span>
          </div>
          <p className="text-xs font-normal text-[#787B7E]">
            Emailing fans who signed up on Punchup is always free.
          </p>
        </div>
      )}
      <div className="rounded-xl border border-gray-200 p-4 sm:p-5 space-y-4">
        {renderSection("subscription", "Monthly Subscription", {
          trailingDivider: true,
        })}
        {renderSection("one_time", "One Time Purchase")}
        {/* One flow child, so the collapsed checkout adds no space-y gap of its own. */}
        <div>
          {activePurchaseMode !== null && (
            <div
              data-testid="checkout-region"
              className={collapseClass(!checkoutActive)}
              aria-hidden={!checkoutActive}
            >
              <div className="overflow-hidden">
                <div
                  className={cn(
                    isDesktop !== false && !paymentSuccess && "pb-4"
                  )}
                >
                  {renderCheckoutContent()}
                </div>
              </div>
            </div>
          )}
          {(isDesktop !== false || !checkoutActive || paymentSuccess) &&
            renderCTA()}
        </div>
      </div>
      {isDesktop === false &&
        checkoutActive &&
        !paymentSuccess &&
        (footerPortalRef?.current
          ? createPortal(
              <div className="bg-white pt-2 pb-2 px-4 border-t border-gray-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                {renderCTA()}
              </div>,
              footerPortalRef.current
            )
          : null)}
      {isDesktop !== false && isTierChange && (
        <TierChangeConfirmDialog
          open={showTierChangeConfirm}
          onOpenChange={setShowTierChangeConfirm}
          direction={isUpgrade ? "upgrade" : "downgrade"}
          cards={subscriptionCards}
          renewalDate={activeSubscription?.renewalDate ?? null}
          onConfirm={confirmTierChange}
          previewedAmountCents={previewedAmountCents}
          previewStatus={previewStatus}
        />
      )}
    </div>
  );
}
